import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import comboComponent from "@/components/child/form/combo.vue";
import dataTooltipComponent from "@/components/child/form/datatooltip.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridColumn, GridColumnType } from "@/components/child/grid/gridColumn";
import multiSelectComponent from "@/components/child/multiSelect/multiSelect.vue";
import ShortcutComponent from "@/components/parent/shortcut/shortcut";
import shortcutComponent from "@/components/parent/shortcut/shortcut.vue";
import { Component, Watch } from "@/decorators";
import GradePCPRequestModel from "@/models/gradePCPRequestModel";
import ManipulacaoOrdemPCPModel from "@/models/pcp/manipulacaoOrdemPCPModel";
import UsuarioModel from "@/models/usuarioModel";
import formaFarmaceuticaService from "@/services/formaFarmaceuticaService";
import GradePCPService from "@/services/gradePCPService";
import setorService from "@/services/setorService";
import tipoVendaService from "@/services/tipoVendaService";
import { AppState, Getters, SessionActions } from "@/store/store";
import { applyTimeZoneDifference, editHourAndMinutes } from "@/utils/common/date";
import Delay from "@/utils/common/delay";
import "../crud/crud.scss";
import "./view.scss";

class GradePCPType {
    dias: string[];
    capacidades: {
        id: number;
        descricao: string;
        horario: string;
        horarioFinalDeSemana: string;
        capacidadesDia: {
            capacidade: number;
            totalVendas: number;
            pendente: number;
        };
    }[];
}

@Component({
    components: {
        dataTooltipComponent,
        actionBarComponent,
        checkboxComponent,
        multiSelectComponent,
        comboComponent,
        dateTimePickerComponent,
        shortcutComponent,
        gridComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["GET_IS_ESTEREIS", "GET_IS_FRACIONAMENTO"] as Getters),
    },
})
export default class GradePCPComponent extends Vue {
    // State computed props
    usuarioLogado: UsuarioModel;
    GET_IS_FRACIONAMENTO: () => Promise<boolean>;
    GET_IS_ESTEREIS: () => Promise<boolean>;

    private shortcutComponent: ShortcutComponent = null;
    private service = new GradePCPService();
    private model = new GradePCPType();
    private requestModel = new GradePCPRequestModel();
    formasFarmaceuticaOptions: Array<Object> = [];
    formasFarmaceuticasIds: Array<Object> = [];
    tiposVendasOptions: Array<Object> = [];
    tipoVendaId = [];

    isFracionamento = false;
    isEstereis = false;
    isLoading = false;
    loadingMounted = false;
    setorOptions: Array<Object> = [];

    manipulacaoOrdemPCPList: ManipulacaoOrdemPCPModel[] = [];

    gridColumnsManipalacoesGradePCP: Array<GridColumn> = [
        new GridColumn("vendaCodigo", "Cód. Venda", GridColumnType.String),
        new GridColumn("manipulacaoOrdemCodigo", "Cód. Manipulação", GridColumnType.Integer),
        new GridColumn("dataRetirada", "Data da retirada", GridColumnType.DateTime),
        new GridColumn("descricaoEtapa", "Etapa atual", GridColumnType.String),
        new GridColumn("quantidade", "Quantidade", GridColumnType.Integer),
    ];

    capacidades = [];
    total: GradePCPType = null;

    private async load() {
        if (!this.isLoading) {
            this.isLoading = true;

            try {
                this.requestModel.formasFarmaceuticasIds = this.formasFarmaceuticasIds.map(p => p["value"]);

                this.requestModel.tipoVendas = (this.tipoVendaId || []).map(p => p["value"]);

                this.model = await this.service
                    .getGradePCP(this.requestModel)
                    .withLoading()
                    .resolveWithJSON<GradePCPType>();

                for (let i = 0; i < this.model.capacidades.length; i++) {
                    this.model.capacidades[i].horario = editHourAndMinutes(
                        applyTimeZoneDifference(new Date(this.model.capacidades[i].horario), true),
                    );
                    this.model.capacidades[i].horarioFinalDeSemana = editHourAndMinutes(
                        applyTimeZoneDifference(new Date(this.model.capacidades[i].horarioFinalDeSemana), true),
                    );
                }

                this.capacidades = [...this.model.capacidades];
                this.total = this.capacidades.pop();
            } catch {
            } finally {
                this.isLoading = false;
            }
        }
    }

    private async mounted() {
        this.isFracionamento = await this.GET_IS_FRACIONAMENTO();
        this.isEstereis = await this.GET_IS_ESTEREIS();
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);

        this.setorOptions = await setorService.comboCached(this.usuarioLogado.franquiaId);
        this.tiposVendasOptions = await tipoVendaService.comboCached(this.usuarioLogado.franquiaId);
        this.formasFarmaceuticaOptions = await formaFarmaceuticaService.comboCached(
            this.usuarioLogado.franquiaId,
            true,
        );
        this.tipoVendaId.push(this.tiposVendasOptions.find(p => p["utilizaValorCusto"]));

        this.loadingMounted = true;
        this.load();
    }

    private async onAtualizar() {
        await this.load();
    }

    private async onClickManipulacao(capacidadeId: number, data: Date, isPendente: boolean) {
        this.requestModel.formasFarmaceuticasIds = this.formasFarmaceuticasIds.map(p => p["value"]);
        this.requestModel.tipoVendas = (this.tipoVendaId || []).map(p => p["value"]);
        this.requestModel.capacidadeId = capacidadeId;
        this.requestModel.dataSelecionada = data;
        this.requestModel.isPendente = isPendente;

        this.manipulacaoOrdemPCPList = await this.service
            .getManipulacoesPorPeriodo(this.requestModel)
            .withLoading()
            .resolveWithJSON<Array<ManipulacaoOrdemPCPModel>>();

        while (!this.$refs.shortcutComponent) await Delay(100);

        this.shortcutComponent = this.$refs.shortcutComponent as ShortcutComponent;
        this.shortcutComponent.title = this.$t("__.ts.manipulacoes").toString();
        this.shortcutComponent.show();
    }
}
